import type { StudentOverview } from '../../api/types';
import { useStudentCommitments } from '../../api/hooks';
import AlertSeverityBadge from '../../components/AlertSeverityBadge';
import { EmptyState } from '../../components/ui';
import AlertPayloadDetail from '../../components/ui/AlertPayloadDetail';
import { FaBell } from '../../components/ui/icons';
import Box from './components/Box';
import { PagedFooter, TextLink } from '../../components/ui';
import { usePagination } from '../../lib/usePagination';
import { dayMonth, parseDate } from './lib/format';
import RiskScorePanel from './components/RiskScorePanel';
import WeeklySummaryPanel from './components/WeeklySummaryPanel';
import StudyPlanPanel from './components/StudyPlanPanel';
import AnomalyPanel from './components/AnomalyPanel';
import Top3WeakChapters from './components/Top3WeakChapters';
import RecentActivityCard from './components/RecentActivityCard';

/** "Today" for an alert fired today, otherwise "3 Oct". */
function firedLabel(iso: string): string {
  const d = parseDate(iso);
  if (!d) return '';
  return d.toDateString() === new Date().toDateString() ? 'Today' : dayMonth(iso);
}

interface Props { id: number; overview: StudentOverview; onSeePerformance?: () => void; onSeePlan?: () => void; }

export default function OverviewTab({ id, overview, onSeePerformance, onSeePlan }: Props) {
  const commitments = useStudentCommitments(id);
  const p = overview.profile;
  const alerts = overview.open_alerts;
  const alertsPg = usePagination(alerts, 5, id);
  // Artboard `.g2` rows: two equal columns, each row's cards side by side.
  const ROW = 'grid items-start gap-4 min-[1180px]:grid-cols-2';
  return (
    <div className="space-y-4">
      <div className={ROW}>
        <RiskScorePanel risk={overview.risk ?? null} />
        <WeeklySummaryPanel summary={overview.weekly_summary ?? null} studentId={id} />
      </div>

      <div className={ROW}>
        <Box title="Open alerts" help="open_alerts"
          actions={alerts.length > 0 ? (
            <TextLink to="/alerts">Alerts inbox →</TextLink>
          ) : undefined}>
          {alerts.length === 0 ? (
            <EmptyState compact icon={<FaBell />} title="No open alerts" body="Nothing needs attention right now." />
          ) : (<>
            <ul>
              {alertsPg.pageItems.map((a) => (
                <li key={a.id} className="flex items-start gap-3 border-b border-hairline py-3 first:pt-0 last:border-b-0 last:pb-0">
                  <span aria-hidden="true" className={`w-1 flex-none self-stretch rounded-sm ${a.severity === 'critical' ? 'bg-danger' : a.severity === 'warning' ? 'bg-idle' : 'bg-slate'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-ink">{a.rule_name}</span>
                      <AlertSeverityBadge severity={a.severity} />
                    </div>
                    <div className="mt-[3px] text-[13px] text-muted"><AlertPayloadDetail alert={a} compact /></div>
                  </div>
                  <span className="whitespace-nowrap text-xs text-muted">{firedLabel(a.fired_at)}</span>
                </li>
              ))}
            </ul>
            {alertsPg.pageCount > 1 && <PagedFooter className="mt-3" label="Open alerts pagination" {...alertsPg} onPage={alertsPg.setPage} />}
          </>)}
        </Box>
        <Top3WeakChapters studentId={id} studentName={p.first_name} onSeePerformance={onSeePerformance} />
      </div>

      <div className={ROW}>
        <AnomalyPanel anomalies={overview.anomalies ?? []} gender={p.gender} />
        <StudyPlanPanel plan={overview.study_plan ?? null} commitment={commitments.data?.active?.[0] ?? null} onOpenPlan={onSeePlan} />
      </div>

      <div className={ROW}>
        <RecentActivityCard studentId={id} />
        <Box title="Profile" help="profile" actions={<span className="text-xs text-muted">from the student app</span>}>
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-[13px]">
            <dt className="text-muted">Motivation</dt>
            <dd className="font-medium text-ink">{p.motivation ?? '—'}</dd>
            <dt className="text-muted">Learning style</dt>
            <dd className="font-medium text-ink">{p.learning_style ?? '—'}</dd>
            <dt className="text-muted">Region</dt>
            <dd className="font-medium text-ink">{p.region ?? '—'}</dd>
            <dt className="text-muted">Language</dt>
            <dd className="font-medium text-ink">{p.language_pref}</dd>
            <dt className="text-muted">Target universities</dt>
            <dd className="font-medium text-ink">{p.target_universities.join(', ') || '—'}</dd>
            <dt className="text-muted">Target departments</dt>
            <dd className="font-medium text-ink">{p.target_departments.join(', ') || '—'}</dd>
          </dl>
        </Box>
      </div>
    </div>
  );
}
